import type { ComponentType } from 'react';
import SkeletonLoader from '../../skeletonLoader/SkeletonLoader';
import { TimeCell } from './TimeCell';
import type { BlockDragListeners, CalendarDay } from './types';

export interface RenderBlock {
    Element: ComponentType<Record<string, unknown>> & { displayName?: string };
    elementProps?: Record<string, unknown>;
    props: Record<string, unknown>;
}

interface ExtendedRenderBlock extends RenderBlock {
    dayName: string;
    index: number;
}

interface BlockProps {
    Element: ComponentType<Record<string, unknown>> & { displayName?: string };
    block: ExtendedRenderBlock;
    elementProps?: Record<string, unknown>;
}

function Block({ Element, block, elementProps }: BlockProps): JSX.Element {
    const id = `${Element.name}_${block.dayName}_${block.index}`;
    return <Element key={id} id={id} block={block.props} {...elementProps} {...block.props} />;
}

// Manages a single row of the calendar, containing time cells and blocks.
interface CalendarRowProps extends BlockDragListeners {
    day: CalendarDay;
    timeColumns: string[];
    loading?: boolean;
    buildMode?: boolean;
    allBlocksPerDay: Map<CalendarDay, RenderBlock[]>;
}

export function CalendarRow({
    day,
    timeColumns,
    loading = false,
    buildMode = false,
    blockDragStart = () => {},
    blockDragMove = () => {},
    blockDragEnd = () => {},
    allBlocksPerDay,
}: CalendarRowProps): JSX.Element {
    return (
        <div
            key={day.key}
            className="relative flex"
            onMouseDown={(event) => buildMode && blockDragStart(day, event)}
            onMouseMove={(event) => buildMode && blockDragMove(day, event)}
            onMouseUp={(event) => buildMode && blockDragEnd(day, event)}
        >
            {loading ? (
                <SkeletonLoader wrapperClassName={'h-20 flex-1 p-1'} className={'h-full p-1'} />
            ) : (
                <>
                    {timeColumns.map((time) => (
                        <TimeCell key={time} dayKey={day.key} time={time} />
                    ))}
                    {(allBlocksPerDay.get(day) ?? []).map((block, index) => (
                        <Block
                            key={`${day.key}_${index}`}
                            Element={block.Element}
                            block={{ ...block, dayName: day.name, index }}
                            elementProps={block.elementProps}
                        />
                    ))}
                </>
            )}
        </div>
    );
}
